import { Link } from "react-router-dom";
import { MessageCircle, Instagram, Bike, ChevronDown } from "lucide-react";
import { SITE, FAQS } from "../data/site";
import { SectionHead, VertKanji } from "../components/Chrome";

function ContactCard({ icon: Icon, title, desc, action, actionLabel, testId, external }) {
  return (
    <article data-testid={testId} className="flex flex-col border-2 border-ink bg-white p-7 shadow-[8px_8px_0_#E4DFD3]">
      <Icon className="h-6 w-6 text-hanko" aria-hidden="true" />
      <h2 className="mt-4 font-display text-xl font-bold text-ink">{title}</h2>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-sumi">{desc}</p>
      {action ? (
        <a
          href={action}
          target={external ? "_blank" : undefined}
          rel={external ? "noopener noreferrer" : undefined}
          data-testid={`${testId}-action`}
          className="mt-5 inline-flex items-center justify-center gap-2 bg-ink px-5 py-3 text-sm font-bold text-paper transition-colors duration-200 hover:bg-hanko"
        >
          {actionLabel}
        </a>
      ) : null}
    </article>
  );
}

export default function KontakPage() {
  return (
    <>
      <section className="border-b-2 border-ink bg-parchment" data-testid="kontak-hero">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-24">
          <nav aria-label="Breadcrumb" className="text-xs font-semibold uppercase tracking-[0.25em] text-sumi">
            <Link to="/" data-testid="breadcrumb-home" className="transition-colors hover:text-hanko">
              Beranda
            </Link>
            <span aria-hidden="true"> / </span>
            <span className="text-hanko">Kontak & FAQ</span>
          </nav>
          <div className="mt-6 flex items-start justify-between gap-6">
            <div>
              <h1 className="font-display text-4xl font-bold tracking-tight text-ink sm:text-5xl">
                Kontak Umaku Sushi
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-sumi">
                Ada pertanyaan soal menu, reservasi, atau waiting list? Hubungi kami lewat kanal resmi berikut.
              </p>
            </div>
            <VertKanji text="連絡先" className="hidden text-2xl font-bold text-ink/15 lg:block" />
          </div>
        </div>
      </section>

      <section className="bg-paper py-14 lg:py-20" data-testid="kontak-channels">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="grid gap-8 md:grid-cols-3">
            <ContactCard
              icon={MessageCircle}
              title="WhatsApp Bintaro"
              desc={`Waiting list dan pemesanan cabang Bintaro melalui ${SITE.whatsappDisplay}. Disarankan menghubungi lebih dulu saat jam makan siang.`}
              action={SITE.whatsappBintaro}
              actionLabel="Chat WhatsApp"
              testId="contact-card-whatsapp"
              external
            />
            <ContactCard
              icon={Instagram}
              title="Instagram Resmi"
              desc="Kabar cabang baru, promo, dan menu terbaru diumumkan pertama kali di Instagram resmi kami."
              action={SITE.instagram}
              actionLabel={SITE.instagramHandle}
              testId="contact-card-instagram"
              external
            />
            <ContactCard
              icon={Bike}
              title="Pesan Antar"
              desc="Umaku Sushi tersedia di aplikasi pesan-antar seperti GrabFood dan ShopeeFood. Cari “Umaku Sushi” di aplikasi favorit Anda."
              action={null}
              actionLabel={null}
              testId="contact-card-delivery"
            />
          </div>
        </div>
      </section>

      <section className="border-t border-sand bg-paper pb-16 lg:pb-24" data-testid="faq-section">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-12">
          <SectionHead overline="FAQ" title="Pertanyaan yang Sering Diajukan" jp="質問" className="pt-14" />
          <div className="mt-10 divide-y divide-sand border-y border-sand">
            {FAQS.map((faq, i) => (
              <details key={faq.q} data-testid={`faq-item-${i + 1}`} className="group">
                <summary
                  data-testid={`faq-trigger-${i + 1}`}
                  className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 font-display text-lg font-bold text-ink transition-colors hover:text-hanko [&::-webkit-details-marker]:hidden"
                >
                  {faq.q}
                  <ChevronDown
                    className="h-5 w-5 shrink-0 text-hanko transition-transform duration-200 group-open:rotate-180"
                    aria-hidden="true"
                  />
                </summary>
                <p className="pb-6 pr-8 text-sm leading-relaxed text-sumi">{faq.a}</p>
              </details>
            ))}
          </div>
          <p className="mt-8 text-sm text-sumi">
            Tidak menemukan jawaban Anda?{" "}
            <a
              href={SITE.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="faq-instagram-link"
              className="font-bold text-hanko underline underline-offset-4"
            >
              Kirim pesan via Instagram
            </a>{" "}
            — tim kami akan membantu.
          </p>
        </div>
      </section>
    </>
  );
}
